import { Skeleton } from 'primeng/skeleton';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Textarea } from 'primeng/textarea';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  CARE_PLAN_CLOSED_REASON_LABEL,
  CarePlanClosedReason,
} from '../../care-plan.model';
import { CarePlanStore } from '../../care-plan.store';
import { CarePlanAnalytics } from '../../analytics/care-plan-analytics';
import { caseApiErrorMessage, caseApiErrorStatus } from '../api/case-api-error';

export interface CloseCaseDialogData {
  store: CarePlanStore;
  caseId: string;
}

const REASON_OPTIONS: CarePlanClosedReason[] = [
  'achieved',
  'abandoned',
  'no_longer_eligible',
  'cancelled',
];

/** "Close this case" — reason required, optional note (D18 unsaved-changes guard applies). */
@Component({
  selector: 'bn-close-case-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Skeleton, FormsModule, Button, ConfirmDialog, Textarea],
  providers: [ConfirmationService],
  templateUrl: './close-case-dialog.component.html',
  styleUrl: './close-case-dialog.component.scss',
})
export class CloseCaseDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);
  private readonly _confirmation = inject(ConfirmationService);
  private readonly _analytics = inject(CarePlanAnalytics);
  private readonly _messages = inject(MessageService, { optional: true });
  private readonly _data = this._config.data as CloseCaseDialogData;

  protected readonly store = this._data.store;
  protected readonly reasonOptions = computed(() =>
    this.store.isLive()
      ? (this.store.closedReasons() ?? []).map((item) => ({
          code: item.code,
          label: item.value,
        }))
      : REASON_OPTIONS.map((code) => ({
          code,
          label: CARE_PLAN_CLOSED_REASON_LABEL[code],
        })),
  );
  protected readonly canSubmit = computed(
    () =>
      !!this.reason() &&
      !this.saving() &&
      !this.store.isCaseSaving(this._data.caseId) &&
      (!this.store.isLive() ||
        (!this.store.closedReasonsLoading() &&
          !this.store.closedReasonsError())) &&
      this.reasonOptions().some((item) => item.code === this.reason()),
  );

  constructor() {
    if (this.store.isLive()) void this.store.loadClosedReasons();
  }

  readonly reason = signal<string | null>(null);
  readonly note = signal('');
  readonly submitAttempted = signal(false);
  readonly saving = signal(false);
  readonly caseSaving = computed(
    () => this.saving() || this.store.isCaseSaving(this._data.caseId),
  );

  protected readonly reasonError = computed(() =>
    this.submitAttempted() && !this.reason()
      ? 'Select a reason to close this case'
      : null,
  );

  protected readonly dirty = computed(
    () => this.reason() !== null || this.note().trim().length > 0,
  );

  selectReason(reason: string): void {
    this.reason.set(reason);
  }

  onReasonKeydown(event: KeyboardEvent, index: number): void {
    if (!this.reasonOptions().length) return;
    const lastIndex = this.reasonOptions().length - 1;
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % this.reasonOptions().length;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex =
        (index - 1 + this.reasonOptions().length) % this.reasonOptions().length;
    }
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;
    if (nextIndex === null) return;

    event.preventDefault();
    this.reason.set(this.reasonOptions()[nextIndex].code);
    const group = (event.currentTarget as HTMLElement).parentElement;
    const radios = group?.querySelectorAll<HTMLElement>('[role="radio"]');
    radios?.[nextIndex]?.focus();
  }

  async confirmClose(): Promise<void> {
    this.submitAttempted.set(true);
    const reason = this.reason();
    if (!reason || !this.canSubmit()) return;
    const note = this.note().trim() || undefined;
    this.saving.set(true);
    try {
      if (this.store.isLive()) {
        await this.store.transitionLiveCaseStatus(this._data.caseId, 'closed', {
          closedReasonCode: reason,
          reason: note,
        });
      } else {
        this.store.closeCase(
          this._data.caseId,
          reason as CarePlanClosedReason,
          note,
        );
      }
      this._analytics.caseClosed(this._data.caseId, reason, !!note);
      this._ref.close({ closed: true });
    } catch (error) {
      // ApiService already emits the transport-level HTTP 500 toast.
      if (caseApiErrorStatus(error) !== 500) {
        this._messages?.add({
          severity: 'error',
          summary: "Couldn't close case",
          detail: caseApiErrorMessage(
            error,
            'The case could not be closed. Try again.',
          ),
        });
      }
    } finally {
      this.saving.set(false);
    }
  }

  tryClose(): void {
    if (this.saving()) return;
    if (!this.dirty()) {
      this._ref.close();
      return;
    }
    this._confirmation.confirm({
      message:
        'You have unsaved changes — are you sure you want to discard them?',
      header: 'Unsaved changes',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectLabel: 'Stay',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this._ref.close(),
    });
  }
}
